import {ChangeDetectionStrategy, Component, OnInit, inject, signal} from '@angular/core';
import {PageTitleComponent} from "../../components/page-title/page-title.component";
import {ToggleSwitch} from "primeng/toggleswitch";
import {Divider} from "primeng/divider";
import {ActivatedRoute, Router} from "@angular/router";
import {AnonymousNotificationPreferencesService} from "../../services/anonymous-notification-preferences.service";
import {AnonymousNotificationPreferencesDto} from "homey-housing-lottery-app-dto";
import {firstValueFrom} from "rxjs";
import {FormsModule} from "@angular/forms";
import {SkeletonModule} from "primeng/skeleton";
import {MessageService} from "primeng/api";

@Component({
  selector: 'homey-public-notification-settings-page',
  standalone: true,
  imports: [PageTitleComponent, ToggleSwitch, FormsModule, SkeletonModule, Divider],
  templateUrl: './public-notification-settings-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PublicNotificationSettingsPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly preferencesService = inject(AnonymousNotificationPreferencesService);
  private readonly messageService = inject(MessageService);

  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly preferences = signal<AnonymousNotificationPreferencesDto | null>(null);

  private anonymousId: string | null = null;

  async ngOnInit(): Promise<void> {
    const id = this.route.snapshot.queryParamMap.get('id');
    if (!id) {
      console.error('Missing anonymous id for public notification settings');
      this.messageService.add({
        severity: 'error',
        summary: 'Sign in required',
        detail: 'Log in to edit notification settings',
        life: 5000
      });
      void this.router.navigate(['/']);
      return;
    }

    this.anonymousId = id;
    await this.loadPreferences();
  }

  protected async onPromotionalChange(enabled: boolean) {
    if (!this.anonymousId) {
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    const current = this.preferences();
    const categories = {
      core: current?.categories.core ?? true,
      lottery: current?.categories.lottery ?? true,
      promotional: enabled,
    };

    try {
      const updated = await firstValueFrom(this.preferencesService.updatePreference({
        anonymousId: this.anonymousId,
        categories,
      }));
      this.preferences.set(updated);
    } catch (err) {
      console.error('Failed to update notification preference', err);
      this.error.set('We could not save your choice. Please try again.');
    } finally {
      this.saving.set(false);
    }
  }

  private async loadPreferences() {
    if (!this.anonymousId) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    try {
      const preferences = await firstValueFrom(this.preferencesService.getPreferences(this.anonymousId));
      this.preferences.set(preferences);
    } catch (err) {
      console.error('Failed to load notification preferences', err);
      this.error.set('We could not load your notification settings. The link may be invalid or expired.');
    } finally {
      this.loading.set(false);
    }
  }
}
